import { useState, useMemo, useCallback } from 'react'
import { saveAs } from 'file-saver'
import { enqueueSnackbar } from 'notistack'
import { GridSortModel } from '@mui/x-data-grid'
import dayjs from 'lib/dayjs'

import { useInventoryReport } from './InventoryReportProvider'
import { useInventoryReportActions } from '../../store/inventoryReport'
import useUpdateParValue from 'lib/services/api/product-service/updateParValue'
import useGetInventoryReport from 'lib/services/api/product-service/getInventoryReport'
import { FiltersParams } from './types/InventoryReport.types'
import { GET_INVENTORY_REPORT } from 'lib/services/api/schema'

export const useLogic = () => {
  const { filters, setFilters, setParValueModal } = useInventoryReport()
  const { updateFilters } = useInventoryReportActions()
  const [updateParValue, { loading: isLoadingUpdateParValue }] =
    useUpdateParValue()

  const [filterHistory, setFilterHistory] = useState<FiltersParams[]>([{}])
  const [currentFilterIndex, setCurrentFilterIndex] = useState(0)

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  })

  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'description', sort: 'asc' },
  ])

  const {
    data,
    loading: isLoadingReport,
    error: reportError,
  } = useGetInventoryReport({
    limit: 500,
    productName: filters.productName,
  })

  const originalAssets = useMemo(() => {
    return data?.getInventoryReport?.items || []
  }, [data])

  const assets = useMemo(() => {
    let filteredAssets = originalAssets

    if (filters?.selectedCompanies?.length) {
      filteredAssets = filteredAssets.filter((asset: any) =>
        filters.selectedCompanies?.includes(asset.vendorName || '')
      )
    }

    if (filters?.selectedLocations?.length) {
      filteredAssets = filteredAssets.filter((asset: any) =>
        asset.inventoryLocationsNames?.some((location: string) =>
          filters.selectedLocations?.includes(location)
        )
      )
    }

    if (filters?.type?.length) {
      filteredAssets = filteredAssets.filter((asset: any) =>
        filters.type?.includes(asset.type)
      )
    }

    if (filters?.itemDisposition?.length) {
      filteredAssets = filteredAssets.filter((asset: any) =>
        filters.itemDisposition?.includes(asset.disposition)
      )
    }

    if (filters?.onHandFilter?.length) {
      filteredAssets = filteredAssets.filter((asset: any) => {
        const quantity = asset.quantity || 0
        const parValue = asset.parValue || 0

        return filters.onHandFilter?.some((filter) => {
          switch (filter) {
            case 'Above Par':
              return quantity >= parValue
            case 'Below Par':
              return quantity < parValue
            default:
              return true
          }
        })
      })
    }

    return filteredAssets
  }, [
    originalAssets,
    filters?.selectedCompanies,
    filters?.selectedLocations,
    filters?.type,
    filters?.itemDisposition,
    filters?.onHandFilter,
  ])

  const paginatedAssets = assets

  const totalCount = useMemo(() => {
    return assets.length
  }, [assets])

  const handleFiltersChange = useCallback(
    (newFilters: Partial<FiltersParams>) => {
      const updatedFilters = { ...filters, ...newFilters }

      const currentFilter = filterHistory[currentFilterIndex]
      const isNewFilter =
        JSON.stringify(currentFilter) !== JSON.stringify(updatedFilters)

      if (isNewFilter) {
        const newHistory = filterHistory.slice(0, currentFilterIndex + 1)
        newHistory.push(updatedFilters)
        setFilterHistory(newHistory)
        setCurrentFilterIndex(newHistory.length - 1)
      }

      updateFilters(newFilters)
      setPaginationModel((prev) => ({ ...prev, page: 0 }))
    },
    [filters, filterHistory, currentFilterIndex, updateFilters]
  )

  const handlePaginationModelChange = useCallback(
    (newModel: typeof paginationModel) => {
      setPaginationModel(newModel)
    },
    []
  )

  const handleSortModelChange = useCallback((newModel: GridSortModel) => {
    setSortModel(newModel)
  }, [])

  const goBack = useCallback(() => {
    if (currentFilterIndex > 0) {
      const prevFilter = filterHistory[currentFilterIndex - 1]
      setFilters(prevFilter)
      setCurrentFilterIndex(currentFilterIndex - 1)
      setPaginationModel((prev) => ({ ...prev, page: 0 }))
    }
  }, [currentFilterIndex, filterHistory, setFilters])

  const goForward = useCallback(() => {
    if (currentFilterIndex < filterHistory.length - 1) {
      const nextFilter = filterHistory[currentFilterIndex + 1]
      setFilters(nextFilter)
      setCurrentFilterIndex(currentFilterIndex + 1)
      setPaginationModel((prev) => ({ ...prev, page: 0 }))
    }
  }, [currentFilterIndex, filterHistory, setFilters])

  const canGoBack = useCallback(
    () => currentFilterIndex > 0,
    [currentFilterIndex]
  )
  const canGoForward = useCallback(
    () => currentFilterIndex < filterHistory.length - 1,
    [currentFilterIndex, filterHistory]
  )

  const resetFilters = useCallback(() => {
    const emptyFilter = {}
    setFilters(emptyFilter)
    setFilterHistory([emptyFilter])
    setCurrentFilterIndex(0)
    setPaginationModel({ page: 0, pageSize: 25 })
  }, [setFilters])

  const handleDownload = useCallback(async () => {
    try {
      if (!assets || assets.length === 0) {
        enqueueSnackbar('No data available to download', {
          variant: 'error',
        })
        return
      }

      const headers = [
        'Product Description',
        'Manufacturer',
        'Model Number',
        'Catalog Number',
        'Type',
        'Ownership',
        'PAR Value',
        'On Hand',
        'Locations',
        'Expiration',
      ]

      const csvData = assets.map((asset) => [
        asset.description || '-',
        asset.vendorName || '-',
        asset.versionModelNumber || '-',
        asset.catalogNumber || '-',
        asset.type || '-',
        asset.disposition || '-',
        asset.parValue?.toString() || '-',
        asset.quantity?.toString() || '-',
        (asset.inventoryLocationsNames || []).join('; ') || '-',
        asset.earliestExpirationDate
          ? dayjs(asset.earliestExpirationDate).format('MM/DD/YYYY')
          : '-',
      ])

      const csvContent = [headers, ...csvData]
        .map((row) =>
          row.map((field) => `"${String(field).replace(/"/g, '""')}"`).join(',')
        )
        .join('\n')

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-')
      const filename = `inventory-report-${timestamp}.csv`

      saveAs(blob, filename)

      enqueueSnackbar(
        `CSV file downloaded successfully (${assets.length} filtered records)`,
        {
          variant: 'success',
        }
      )
    } catch (error) {
      console.error('Error generating CSV:', error)
      enqueueSnackbar('Error generating CSV file', {
        variant: 'error',
      })
    }
  }, [assets])

  const handleUpdateParValue = async (parValueId: number, parValue: number) => {
    try {
      await updateParValue({
        variables: {
          newParValue: parValue || 0,
          parValueId: parValueId,
        },
        refetchQueries: [
          {
            query: GET_INVENTORY_REPORT,
            variables: {
              limit: 500,
              productName: filters.productName,
            },
          },
        ],
        awaitRefetchQueries: true,
      })

      setParValueModal({ open: false })
    } catch (e) {
      console.error('Failed to update par value', e)
    }
  }

  return {
    assets: paginatedAssets,
    originalAssets,
    totalCount,
    isLoadingReport,
    reportError,
    paginationModel,
    handlePaginationModelChange,
    sortModel,
    handleSortModelChange,
    filters,
    handleFiltersChange,
    goBack,
    goForward,
    canGoBack,
    canGoForward,
    resetFilters,
    handleDownload,
    handleUpdateParValue,
    isLoadingUpdateParValue,
  }
}

export default useLogic
